import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Menu } from "@devices/components/Header/Menu";
import { Box, Theme, Typography } from "@mui/material";
import { CheckboxField } from "@shared/form/CheckboxField";
import { QueryBuilderField } from "@shared/form/QueryBuilderField/QueryBuilderField";
import QueryBuilder, { ActiveFilter } from "@shared/form/QueryBuilderField/QueryBuilderField.utils";
import { Table } from "@tanstack/react-table";

import DropdownContainer from "../shared/Dropdown/DropdownContainer";

import { DeviceTableData } from "./DeviceTable.utils";

const useStyles = makeStyles()((theme: Theme) => ({
    container: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    box: {
        display: "flex",
        justifyContent: "flex-end",
        gap: theme.spacing(1),
    },
    dropdownBox: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: "space-between",
        width: "100%",
        padding: theme.spacing(3, 5),
    },
    checkboxContainer: {
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(1),
    },
    title: {
        display: "inline",
        fontWeight: 700,
    },
    titleContainer: {
        textAlign: "center",
        marginBottom: theme.spacing(1),
    },
    outerContainer: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
}));

type Props = {
    value: string;
    onChange: (value: string) => void;
    onClear: () => void;
    onResetColumns: () => void;
    table: Table<DeviceTableData>;
};
export const ManagementSection = (props: Props) => {
    const [manageAnchor, setManageAnchor] = React.useState<HTMLButtonElement | null>(null);
    const [columnsMenuOpen, setColumnsMenuOpen] = React.useState(false);

    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);

    // Suggestions come from the devices that pass the applied filters and column filters, so every suggestion still
    // finds a device. The table's filtered rows cannot be used: they still include the free-text search that picking a
    // filter drops, because that change only reaches the table on the next render.
    const getDistinctValues = (getValues: (row: DeviceTableData) => (string | null)[]) => (filters: ActiveFilter[]) => {
        const columnFilters = props.table.getState().columnFilters;
        const values = new Set<string>();
        for (const row of props.table.getCoreRowModel().rows) {
            if (
                // The table skips a stored filter whose column no longer exists and records no result for it.
                columnFilters.every((filter) => row.columnFilters[filter.id] !== false) &&
                QueryBuilder.matchesFilters(row.original.queryMap, filters)
            ) {
                getValues(row.original).forEach((value) => value && values.add(value));
            }
        }
        return _.sortBy([...values], (value) => value.toLowerCase());
    };

    // exclude actions columns from the list of columns where visibility can be toggled
    const tableColumns = props.table.getAllColumns().filter((column) => column.columnDef.id !== "moveLicenseCheckbox");
    return (
        <Box className={classes.outerContainer}>
            <Box className={classes.container}>
                <QueryBuilderField
                    value={props.value}
                    placeholder={t("common:button.search")}
                    onChange={props.onChange}
                    onClear={props.onClear}
                    options={[
                        {
                            key: "device.lastState.build.model",
                            label: t("pages:devicePage.filters.deviceModel"),
                            getSuggestions: getDistinctValues((row) => [row.hardwareModel]),
                        },
                        {
                            key: "groupName",
                            label: t("pages:devicePage.filters.groupName"),
                            getSuggestions: getDistinctValues((row) => [row.groupName]),
                        },
                        { key: "device.serial", label: t("pages:devicePage.filters.serial") },
                        { key: "device.lastState.packages.versionCode", label: t("pages:devicePage.filters.applicationVersion") },
                        {
                            key: "device.lastState.packages.package",
                            label: t("pages:devicePage.filters.applicationPackage"),
                            getSuggestions: getDistinctValues((row) =>
                                QueryBuilder.splitStoredValues(row.queryMap["device.lastState.packages.package"]),
                            ),
                        },
                        { key: "chargeId", label: t("pages:devicePage.filters.chargeId") },
                    ]}
                />

                <Box className={classes.box}>
                    <Menu
                        onAnchorChange={setManageAnchor}
                        onSelectColumns={() => setColumnsMenuOpen(true)}
                        onResetColumns={props.onResetColumns}
                    />
                    <DropdownContainer
                        hideTrigger
                        anchorEl={manageAnchor}
                        open={columnsMenuOpen}
                        onOpenChange={setColumnsMenuOpen}
                        listProps={{ eventType: "onKeyDown" }}
                    >
                        <Box className={classes.dropdownBox}>
                            {tableColumns.map((column) => (
                                <Box key={column.id}>
                                    <div className={classes.titleContainer}>
                                        <Typography variant="body1" className={classes.title}>
                                            {column.id}
                                        </Typography>
                                    </div>
                                    {column.columns.map((leafColumn) => (
                                        <Box key={leafColumn.id} className={classes.checkboxContainer}>
                                            <CheckboxField
                                                checked={leafColumn.getIsVisible()}
                                                onChange={() => leafColumn.toggleVisibility()}
                                                label={
                                                    (leafColumn.columnDef.header as string) ||
                                                    t("pages:devicePage.table.header.indicator")
                                                }
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    </DropdownContainer>
                </Box>
            </Box>
        </Box>
    );
};
